import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Filter,
  Download,
  Eye,
  Edit,
  Trash2,
  TrendingDown,
  DollarSign,
  Calendar,
  Building,
  Tag,
  CreditCard,
  FileText,
  CheckCircle,
  Clock,
  XCircle,
  Receipt,
  Repeat
} from 'lucide-react';
import AdminLayout from '../../../components/admin/AdminLayout';
import { useAdminAuth } from '../../../contexts/AdminAuthContext';

const ExpensesPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAdminAuth();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterPeriod, setFilterPeriod] = useState('all');

  // Mock data - سيتم استبداله بـ API
  useEffect(() => {
    setTimeout(() => {
      const mockExpenses = [
        {
          id: 'EXP-2024-001',
          vendor: 'شركة المحاماة الاستشارية',
          category: 'legal',
          amount: 8000,
          date: '2024-12-01',
          paymentMethod: 'bank_transfer',
          invoiceId: 'INV-VENDOR-001',
          caseId: 'WZ-2024-001',
          status: 'paid',
          recurring: false
        },
        {
          id: 'EXP-2024-002',
          vendor: 'شركة البرمجيات المتقدمة',
          category: 'technical',
          amount: 15000,
          date: '2024-12-03',
          paymentMethod: 'credit_card',
          invoiceId: null,
          caseId: null,
          status: 'paid',
          recurring: true
        },
        {
          id: 'EXP-2024-003',
          vendor: 'مكتب التسويق الرقمي',
          category: 'marketing',
          amount: 12000,
          date: '2024-11-28',
          paymentMethod: 'bank_transfer',
          invoiceId: 'INV-VENDOR-003',
          caseId: null,
          status: 'paid',
          recurring: false
        },
        {
          id: 'EXP-2024-004',
          vendor: 'شركة الصيانة والتشغيل',
          category: 'operational',
          amount: 5000,
          date: '2024-12-05',
          paymentMethod: 'cash',
          invoiceId: null,
          caseId: null,
          status: 'under_review',
          recurring: false
        },
        {
          id: 'EXP-2024-005',
          vendor: 'مكتب المحاسبة القانونية',
          category: 'administrative',
          amount: 7500,
          date: '2024-11-20',
          paymentMethod: 'bank_transfer',
          invoiceId: 'INV-VENDOR-005',
          caseId: null,
          status: 'paid',
          recurring: true
        },
        {
          id: 'EXP-2024-006',
          vendor: 'شركة الاستشارات القانونية',
          category: 'legal',
          amount: 10000,
          date: '2024-12-10',
          paymentMethod: 'bank_transfer',
          invoiceId: null,
          caseId: 'WZ-2024-005',
          status: 'cancelled',
          recurring: false
        }
      ];
      setExpenses(mockExpenses);
      setLoading(false);
    }, 500);
  }, []);

  // Calculate statistics
  const stats = {
    total: expenses.filter(e => e.status === 'paid').reduce((sum, e) => sum + e.amount, 0),
    monthly: expenses
      .filter(e => {
        const date = new Date(e.date);
        const now = new Date();
        return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear() && e.status === 'paid';
      })
      .reduce((sum, e) => sum + e.amount, 0),
    byVendor: expenses
      .filter(e => e.status === 'paid')
      .reduce((acc, e) => {
        acc[e.vendor] = (acc[e.vendor] || 0) + e.amount;
        return acc;
      }, {}),
    byCategory: expenses
      .filter(e => e.status === 'paid')
      .reduce((acc, e) => {
        acc[e.category] = (acc[e.category] || 0) + e.amount;
        return acc;
      }, {}),
    recurring: expenses.filter(e => e.recurring && e.status !== 'cancelled').reduce((sum, e) => sum + e.amount, 0)
  };

  // Get top vendor and category
  const topVendor = Object.entries(stats.byVendor).sort((a, b) => b[1] - a[1])[0] || ['لا يوجد', 0];
  const topCategory = Object.entries(stats.byCategory).sort((a, b) => b[1] - a[1])[0] || ['لا يوجد', 0];

  // Category labels
  const categoryLabels = {
    legal: 'قانوني',
    administrative: 'إداري',
    technical: 'تقني',
    marketing: 'تسويق',
    operational: 'تشغيلي'
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      paid: {
        label: 'مدفوع',
        icon: CheckCircle,
        className: 'bg-green-50 text-green-700 border-green-200'
      },
      under_review: {
        label: 'تحت المراجعة',
        icon: Clock,
        className: 'bg-yellow-50 text-yellow-700 border-yellow-200'
      },
      cancelled: {
        label: 'ملغي',
        icon: XCircle,
        className: 'bg-gray-50 text-gray-700 border-gray-200'
      }
    };

    const config = statusConfig[status] || statusConfig.under_review;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold border ${config.className}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  // Get payment method badge
  const getPaymentMethodBadge = (method) => {
    const methodConfig = {
      cash: { label: 'نقداً', className: 'bg-green-50 text-green-700 border-green-200' },
      credit_card: { label: 'بطاقة ائتمانية', className: 'bg-blue-50 text-blue-700 border-blue-200' },
      bank_transfer: { label: 'تحويل بنكي', className: 'bg-purple-50 text-purple-700 border-purple-200' }
    };

    const config = methodConfig[method] || methodConfig.bank_transfer;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold border ${config.className}`}>
        <CreditCard className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  // Filter expenses
  const filteredExpenses = expenses.filter(expense => {
    const matchesSearch =
      expense.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      expense.vendor.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = filterStatus === 'all' || expense.status === filterStatus;
    const matchesCategory = filterCategory === 'all' || expense.category === filterCategory;

    // Period filter
    const matchesPeriod = (() => {
      if (filterPeriod === 'all') return true;
      const date = new Date(expense.date);
      const now = new Date();
      
      if (filterPeriod === 'month') {
        return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
      }
      if (filterPeriod === 'year') {
        return date.getFullYear() === now.getFullYear();
      }
      return true;
    })();

    return matchesSearch && matchesStatus && matchesCategory && matchesPeriod;
  });

  // Handle delete
  const handleDelete = (expenseId) => {
    if (window.confirm('هل أنت متأكد من حذف هذا المصروف؟')) {
      alert(`تم حذف المصروف ${expenseId}`);
    }
  };

  // Handle export
  const handleExport = () => {
    alert('سيتم تصدير تقرير المصروفات قريباً');
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[#1E3A8A] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">جاري تحميل المصروفات...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-black text-[#1E3A8A] mb-2 flex items-center gap-3">
                <TrendingDown className="w-8 h-8" />
                <span>إدارة المصروفات</span>
              </h1>
              <p className="text-gray-600">تتبع وإدارة جميع المصروفات والنفقات</p>
            </div>
            <div className="flex items-center gap-3">
              {hasPermission('finance.export') && (
                <button
                  onClick={handleExport}
                  className="inline-flex items-center gap-2 px-4 py-2 border-2 border-[#1E3A8A] text-[#1E3A8A] rounded-lg font-bold hover:bg-blue-50 transition-all"
                >
                  <Download className="w-5 h-5" />
                  <span>تصدير</span>
                </button>
              )}
              {hasPermission('finance.expense.create') && (
                <button
                  onClick={() => navigate('/admin/finance/expenses/create')}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#1E3A8A] to-[#1E40AF] text-white rounded-lg font-bold hover:shadow-lg transition-all"
                >
                  <Plus className="w-5 h-5" />
                  <span>إضافة مصروف</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* 1️⃣ Stats Dashboard */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          {/* Total Expenses */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">إجمالي المصروفات</p>
                <p className="text-2xl font-black text-red-600">{stats.total.toLocaleString()} ر.س</p>
              </div>
              <DollarSign className="w-10 h-10 text-red-600 opacity-20" />
            </div>
          </div>

          {/* Monthly Expenses */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-orange-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">المصروفات الشهرية</p>
                <p className="text-2xl font-black text-orange-600">{stats.monthly.toLocaleString()} ر.س</p>
              </div>
              <Calendar className="w-10 h-10 text-orange-600 opacity-20" />
            </div>
          </div>

          {/* Top Vendor */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-[#C8A870] p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">أعلى جهة</p>
                <p className="text-sm font-black text-[#C8A870]">{topVendor[0].substring(0, 12)}...</p>
                <p className="text-xs text-gray-500 mt-1">{topVendor[1].toLocaleString()} ر.س</p>
              </div>
              <Building className="w-10 h-10 text-[#C8A870] opacity-20" />
            </div>
          </div>

          {/* Top Category */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-purple-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">أكثر بند صرف</p>
                <p className="text-sm font-black text-purple-600">{categoryLabels[topCategory[0]] || 'لا يوجد'}</p>
                <p className="text-xs text-gray-500 mt-1">{topCategory[1].toLocaleString()} ر.س</p>
              </div>
              <Tag className="w-10 h-10 text-purple-600 opacity-20" />
            </div>
          </div>

          {/* Recurring Expenses */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-blue-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">المصروفات المتكررة</p>
                <p className="text-2xl font-black text-blue-600">{stats.recurring.toLocaleString()} ر.س</p>
              </div>
              <Repeat className="w-10 h-10 text-blue-600 opacity-20" />
            </div>
          </div>
        </div>

        {/* 4️⃣ Filters */}
        <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="البحث برقم أو جهة..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pr-10 pl-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none"
              />
            </div>

            {/* Status Filter */}
            <div className="relative">
              <Filter className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full pr-10 pl-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none appearance-none"
              >
                <option value="all">جميع الحالات</option>
                <option value="paid">مدفوع</option>
                <option value="under_review">تحت المراجعة</option>
                <option value="cancelled">ملغي</option>
              </select>
            </div>

            {/* Category Filter */}
            <div className="relative">
              <Tag className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="w-full pr-10 pl-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none appearance-none"
              >
                <option value="all">جميع التصنيفات</option>
                <option value="legal">قانوني</option>
                <option value="administrative">إداري</option>
                <option value="technical">تقني</option>
                <option value="marketing">تسويق</option>
                <option value="operational">تشغيلي</option>
              </select>
            </div>

            {/* Period Filter */}
            <div className="relative">
              <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <select
                value={filterPeriod}
                onChange={(e) => setFilterPeriod(e.target.value)}
                className="w-full pr-10 pl-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none appearance-none"
              >
                <option value="all">جميع الفترات</option>
                <option value="month">هذا الشهر</option>
                <option value="year">هذا العام</option>
              </select>
            </div>
          </div>
        </div>

        {/* 2️⃣ Expenses Table */}
        <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b-2 border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">رقم العملية</th>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">الجهة</th>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">التصنيف</th>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">المبلغ</th>
                  <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">التاريخ</th>
                  <th className="px-4 py-3 text-center text-sm font-bold text-gray-700">طريقة الدفع</th>
                  <th className="px-4 py-3 text-center text-sm font-bold text-gray-700">الربط</th>
                  <th className="px-4 py-3 text-center text-sm font-bold text-gray-700">الحالة</th>
                  <th className="px-4 py-3 text-center text-sm font-bold text-gray-700">الإجراءات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredExpenses.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="px-4 py-8 text-center text-gray-500">
                      <Receipt className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p>لا توجد مصروفات</p>
                    </td>
                  </tr>
                ) : (
                  filteredExpenses.map((expense) => (
                    <tr key={expense.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-[#1E3A8A]">{expense.id}</span>
                          {expense.recurring && (
                            <Repeat className="w-4 h-4 text-blue-600" title="مصروف متكرر" />
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-semibold text-gray-800">{expense.vendor}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-bold border border-gray-300">
                          <Tag className="w-3 h-3" />
                          {categoryLabels[expense.category]}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-bold text-red-600 text-lg">{expense.amount.toLocaleString()} ر.س</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-700">{expense.date}</span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {getPaymentMethodBadge(expense.paymentMethod)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {expense.invoiceId || expense.caseId ? (
                          <div className="flex flex-col gap-1">
                            {expense.invoiceId && (
                              <button
                                onClick={() => navigate(`/admin/finance/invoices/${expense.invoiceId}`)}
                                className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 font-semibold text-xs transition-colors"
                              >
                                <FileText className="w-3 h-3" />
                                {expense.invoiceId}
                              </button>
                            )}
                            {expense.caseId && (
                              <span className="text-xs text-gray-500">{expense.caseId}</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {getStatusBadge(expense.status)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          {/* View */}
                          <button
                            onClick={() => alert('عرض تفاصيل المصروف')}
                            className="p-2 hover:bg-blue-50 rounded-lg transition-colors"
                            title="عرض التفاصيل"
                          >
                            <Eye className="w-4 h-4 text-blue-600" />
                          </button>

                          {/* Edit */}
                          {hasPermission('finance.expense.edit') && (
                            <button
                              onClick={() => navigate(`/admin/finance/expenses/${expense.id}/edit`)}
                              className="p-2 hover:bg-yellow-50 rounded-lg transition-colors"
                              title="تعديل"
                            >
                              <Edit className="w-4 h-4 text-yellow-600" />
                            </button>
                          )}

                          {/* Delete */}
                          {hasPermission('finance.expense.delete') && (
                            <button
                              onClick={() => handleDelete(expense.id)}
                              className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                              title="حذف"
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Summary Footer */}
        {filteredExpenses.length > 0 && (
          <div className="mt-6 bg-gray-50 rounded-lg p-4 border-2 border-gray-200">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">عرض {filteredExpenses.length} من أصل {expenses.length} مصروف</span>
              <span className="font-bold text-[#1E3A8A]">
                إجمالي المصروفات المعروضة: {filteredExpenses.filter(e => e.status === 'paid').reduce((sum, e) => sum + e.amount, 0).toLocaleString()} ر.س
              </span>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default ExpensesPage;
